import React, { useEffect, useRef } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const toolbarButtonStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid var(--Color-line-normal)',
  background: 'var(--Color-background-normal)',
  cursor: 'pointer',
  fontSize: '13px',
  fontFamily: 'var(--font-family-primary)',
  color: 'var(--Color-text-normal)',
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '본문을 입력하세요…',
  minHeight = '320px',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!editorRef.current || isInternalChange.current) return;
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const sync = () => {
    if (!editorRef.current) return;
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
    requestAnimationFrame(() => {
      isInternalChange.current = false;
    });
  };

  const exec = (command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    sync();
  };

  const insertLink = () => {
    const url = window.prompt('링크 URL을 입력하세요', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  return (
    <div
      style={{
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid var(--Color-line-normal)',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'var(--Color-background-normal)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '10px 12px',
          borderBottom: '1px solid var(--Color-line-normal)',
          background: 'var(--Color-background-main)',
        }}
      >
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('bold')}>
          굵게
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('italic')}>
          기울임
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('formatBlock', 'h2')}>
          제목
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('formatBlock', 'p')}>
          본문
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('insertUnorderedList')}>
          목록
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('insertOrderedList')}>
          번호
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={insertLink}>
          링크
        </button>
        <button type="button" style={toolbarButtonStyle} onClick={() => exec('removeFormat')}>
          서식 지우기
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-multiline
        data-placeholder={placeholder}
        onInput={sync}
        onBlur={sync}
        suppressContentEditableWarning
        style={{
          width: '100%',
          boxSizing: 'border-box',
          minHeight,
          padding: '16px 20px',
          outline: 'none',
          fontSize: '15px',
          lineHeight: 1.7,
          color: 'var(--Color-text-normal)',
          fontFamily: 'var(--font-family-primary)',
        }}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: var(--Color-text-normal);
          opacity: 0.45;
        }
        [contenteditable] h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 1.25em 0 0.5em;
          color: var(--Color-text-strong);
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        [contenteditable] a {
          color: var(--Color-fill-brand-color);
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
